import { useState, useMemo, useCallback } from "react";
import { calculateSeasonElo, compareToOfficial } from "../utils/eloEngine";
import { DEFAULT_PARAMS } from "../utils/eloConstants";

/**
 * React hook for managing ELO rankings with real-time parameter updates
 */
export function useEloRankings(teams, games, officialElo) {
  // Parameter state
  const [params, setParams] = useState(DEFAULT_PARAMS);

  // Current week for timeline scrubbing
  const [currentWeek, setCurrentWeek] = useState(null); // null = final

  // Calculate ELO ratings (memoized - only recalculates when params change)
  const eloResult = useMemo(() => {
    if (!teams?.length || !games?.length) {
      return null;
    }

    // Filter games to only include those with at least one FBS team
    const fbsTeamNames = new Set(teams.map((t) => t.school));
    const fbsGames = games.filter(
      (g) => fbsTeamNames.has(g.homeTeam) || fbsTeamNames.has(g.awayTeam)
    );

    return calculateSeasonElo(teams, fbsGames, params);
  }, [teams, games, params]);

  // Get rankings for current week
  const rankings = useMemo(() => {
    if (!eloResult) return [];

    if (currentWeek === null) {
      return eloResult.rankings;
    }

    return eloResult.weekSnapshots.get(currentWeek) || eloResult.rankings;
  }, [eloResult, currentWeek]);

  // Compare to official rankings
  const comparison = useMemo(() => {
    if (!rankings.length || !officialElo?.length) return null;
    return compareToOfficial(rankings, officialElo);
  }, [rankings, officialElo]);

  // Parameter update functions
  const updateParam = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetParams = useCallback(() => {
    setParams(DEFAULT_PARAMS);
  }, []);

  // Get total weeks available
  const totalWeeks = eloResult?.totalWeeks || 0;

  // Available weeks for timeline
  const availableWeeks = useMemo(() => {
    if (!eloResult) return [];
    return Array.from(eloResult.weekSnapshots.keys()).sort((a, b) => a - b);
  }, [eloResult]);

  return {
    // Data
    rankings,
    comparison,
    totalWeeks,
    availableWeeks,

    // Parameters
    params,
    updateParam,
    resetParams,

    // Timeline
    currentWeek,
    setCurrentWeek,

    // Loading state
    isLoading: !eloResult,
  };
}
